import React, { useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle, AlertOctagon, Info, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const ESTADO = {
  verde: { label: "Funciona", dot: "bg-emerald-500", ring: "border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20 dark:border-emerald-900" },
  ambar: { label: "Con reservas", dot: "bg-amber-500", ring: "border-amber-200 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-900" },
  rojo: { label: "Pendiente", dot: "bg-red-500", ring: "border-red-200 bg-red-50/60 dark:bg-red-950/20 dark:border-red-900" },
};

export const GRAVEDAD = {
  P0: { label: "Bloqueante", cls: "bg-red-600 text-white hover:bg-red-600", Icon: AlertOctagon },
  P1: { label: "Importante", cls: "bg-orange-500 text-white hover:bg-orange-500", Icon: AlertTriangle },
  P2: { label: "Mejora", cls: "bg-amber-400 text-amber-950 hover:bg-amber-400", Icon: Info },
};

function Cifras({ cifras }) {
  return (
    <section data-testid="auditoria-cifras">
      <h2 className="mb-3 font-heading text-lg font-bold">SOFI hoy, en cifras</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {cifras.map((c) => (
          <Card key={c.label} className="border-border/70"><CardContent className="p-3.5">
            <div className="font-heading text-2xl font-bold text-primary">{c.value}</div>
            <div className="text-sm font-medium">{c.label}</div>
            <div className="text-[11px] leading-snug text-muted-foreground">{c.detail}</div>
          </CardContent></Card>
        ))}
      </div>
    </section>
  );
}

function Semaforo({ items }) {
  return (
    <section data-testid="auditoria-semaforo">
      <h2 className="mb-1 font-heading text-lg font-bold">Semáforo por área</h2>
      <p className="mb-3 text-sm text-muted-foreground">Verde: funciona y se usa · Ámbar: funciona con reservas · Rojo: pendiente antes de abrir a todo el equipo.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((a) => {
          const e = ESTADO[a.estado];
          return (
            <div key={a.area} className={`rounded-lg border p-3.5 ${e.ring}`} data-testid={`semaforo-${a.estado}`}>
              <div className="flex items-center gap-2">
                <span className={`h-3 w-3 shrink-0 rounded-full ${e.dot}`} />
                <span className="font-medium">{a.area}</span>
                <Badge variant="outline" className="ml-auto text-[10px]">{e.label}</Badge>
              </div>
              <p className="mt-1.5 text-sm text-foreground/85">{a.texto}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Riesgo({ idx, r, onOpenAnnex }) {
  const [open, setOpen] = useState(false);
  const g = GRAVEDAD[r.gravedad];
  return (
    <Card className="border-border/70" data-testid={`riesgo-${r.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted font-heading text-sm font-bold">{idx}</div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold leading-tight">{r.titulo}</h3>
              <Badge className={`gap-1 ${g.cls}`}><g.Icon className="h-3 w-3" />{r.gravedad} · {g.label}</Badge>
            </div>
            <p className="mt-1.5 text-sm text-foreground/90">{r.que_pasa}</p>
            <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`riesgo-toggle-${r.id}`}
              className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}¿Por qué importa y qué se propone?
            </button>
            {open && (
              <div className="mt-2 space-y-2 rounded-md bg-muted/50 p-3 text-sm" data-testid={`riesgo-detalle-${r.id}`}>
                <p><span className="font-semibold">Por qué importa: </span>{r.por_que_importa}</p>
                <p><span className="font-semibold">Qué se propone: </span>{r.propuesta}</p>
                <button type="button" onClick={() => onOpenAnnex(r.anexo)} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`riesgo-anexo-${r.id}`}>
                  Ver detalle técnico en el anexo <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AuditoriaResumen({ summary, onOpenAnnex }) {
  return (
    <>
      <Card className="border-primary/30 bg-primary/5" data-testid="auditoria-veredicto"><CardContent className="p-5">
        <h2 className="font-heading text-lg font-bold">Veredicto</h2>
        <p className="mt-1.5 text-sm leading-relaxed text-foreground/90">
          SOFI 2.0 es una plataforma real, extensa y operativa: el trabajo diario de viajes, reservas, cotizaciones, leads, facturas, RRHH e informes funciona y responde rápido.
          <strong> Puede abrirse a 20 usuarios en cuanto se resuelvan tres puntos</strong> (copias de seguridad automáticas, PayPal en modo real y cerrar 10 pantallas internas sin contraseña)
          y, en las dos primeras semanas, se refuercen el acceso y las pantallas que cargan demasiados datos. El resto es mantenimiento progresivo que no bloquea el uso.
        </p>
      </CardContent></Card>
      <Cifras cifras={summary.cifras} />
      <Semaforo items={summary.semaforo} />
      <section data-testid="auditoria-riesgos">
        <h2 className="mb-1 font-heading text-lg font-bold">Los 10 riesgos principales</h2>
        <p className="mb-3 text-sm text-muted-foreground">Ordenados por gravedad. Despliega cada uno para ver por qué importa y qué se propone.</p>
        <div className="space-y-3">
          {summary.riesgos.map((r, i) => <Riesgo key={r.id} idx={i + 1} r={r} onOpenAnnex={onOpenAnnex} />)}
        </div>
      </section>
    </>
  );
}
